import { Alert, Button, Group, Text } from "@mantine/core";
import { IconAlertCircle, IconId, IconShieldCheck } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { IDENTITY_KYC_CALLOUT, kycStatusLabel, recruiterNeedsKycPack } from "../lib/kyc";
import type { AffiliateProfile, RecruiterProfile } from "../types/api";

type IdentityProfile = RecruiterProfile | AffiliateProfile;

export function KycStatusCallout({
  profile,
  jobsPath = "/jobs",
  kycPath = "/kyc",
  variant = "recruiter",
}: {
  profile?: IdentityProfile | null;
  jobsPath?: string;
  kycPath?: string;
  variant?: "recruiter" | "affiliate";
}) {
  if (!profile) return null;

  const docsMissing =
    "kyc_documents_complete" in profile ? recruiterNeedsKycPack(profile as RecruiterProfile) : false;

  if (profile.kyc_status === "verified" && docsMissing) {
    return (
      <Alert icon={<IconId size="var(--axio-unit-18)" />} color="blue" title={IDENTITY_KYC_CALLOUT.docsMissingTitle}>
        <Text size="sm">{IDENTITY_KYC_CALLOUT.docsMissingBody}</Text>
        <Button component={Link} to={kycPath} size="xs" mt="sm" color="blue" variant="light">
          {IDENTITY_KYC_CALLOUT.docsMissingCta}
        </Button>
      </Alert>
    );
  }

  if (profile.kyc_status === "verified") return null;

  if (profile.kyc_status === "submitted") {
    return (
      <Alert icon={<IconShieldCheck size="var(--axio-unit-18)" />} color="violet" title={IDENTITY_KYC_CALLOUT.submittedTitle}>
        <Text size="sm">
          {variant === "affiliate"
            ? IDENTITY_KYC_CALLOUT.submittedAffiliateBody
            : IDENTITY_KYC_CALLOUT.submittedRecruiterBody}
        </Text>
        <Group gap="xs" mt="sm">
          <Button component={Link} to={jobsPath} size="xs" variant="light" color="violet">
            {IDENTITY_KYC_CALLOUT.browseJobsCta}
          </Button>
        </Group>
      </Alert>
    );
  }

  if (profile.kyc_status === "rejected") {
    return (
      <Alert icon={<IconAlertCircle size="var(--axio-unit-18)" />} color="red" title={IDENTITY_KYC_CALLOUT.rejectedTitle}>
        <Text size="sm">{profile.kyc_notes || IDENTITY_KYC_CALLOUT.rejectedFallback}</Text>
        <Button component={Link} to={kycPath} size="xs" mt="sm" color="red" variant="light">
          {IDENTITY_KYC_CALLOUT.rejectedCta}
        </Button>
      </Alert>
    );
  }

  return (
    <Alert
      icon={<IconAlertCircle size="var(--axio-unit-18)" />}
      color="yellow"
      title={`KYC: ${kycStatusLabel(profile.kyc_status, "kyc_documents_complete" in profile ? profile.kyc_documents_complete : undefined)}`}
    >
      <Text size="sm">
        {variant === "affiliate" ? IDENTITY_KYC_CALLOUT.pendingAffiliateBody : IDENTITY_KYC_CALLOUT.pendingRecruiterBody}
      </Text>
      <Group gap="xs" mt="sm">
        <Button component={Link} to={kycPath} size="xs" color="yellow">
          {IDENTITY_KYC_CALLOUT.completeCta}
        </Button>
        <Button component={Link} to={jobsPath} size="xs" variant="default">
          {IDENTITY_KYC_CALLOUT.browseJobsCta}
        </Button>
      </Group>
    </Alert>
  );
}
